import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { Home } from 'lucide-react';
import { GlassCard } from './SharedUI';
import { CollapseHeader, CollapseSection } from './NeuralSynthesisMonitor';
import { MijiaDeviceCard, MijiaDevice, AirConditionerState, ThermometerState } from './MijiaDeviceCard';
import { iotService } from '../services/iotService';

const IOT_POLL_MS = 3000;

/**
 * 米家设备面板（独立组件）
 * 从 NeuralSynthesisMonitor 中解耦出来，使"显示米家设备"开关独立于
 * "显示神经合成监控"开关——两者各自受设置控制，互不依赖。
 */
export function MijiaPanel({ t }: { t?: any }) {
  const [iotDevices, setIotDevices] = useState<MijiaDevice[]>([]);
  const [iotCollapsed, setIotCollapsed] = useState(false);
  const [hiddenDeviceIds, setHiddenDeviceIds] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('xf_mijia_hidden_devices');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  // ── IoT devices polling ──
  useEffect(() => {
    const poll = async () => {
      try {
        const devices = await iotService.getDevices();
        setIotDevices(devices);
      } catch {}
    };
    poll();
    const id = setInterval(poll, IOT_POLL_MS);
    return () => clearInterval(id);
  }, []);

  // ── Listen for hidden devices changes from Settings ──
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.key === 'xf_mijia_hidden_devices') {
        setHiddenDeviceIds(detail.value ?? []);
      }
    };
    window.addEventListener('xf:setting-changed', handler);
    return () => window.removeEventListener('xf:setting-changed', handler);
  }, []);

  const handleIotTogglePower = useCallback(async (id: string, powerOn: boolean) => {
    // Bug 19: 添加 try/catch 错误处理
    try {
      await iotService.togglePower(id, powerOn);
      const devices = await iotService.getDevices();
      setIotDevices(devices);
    } catch (err) {
      console.error('[MijiaPanel] togglePower failed:', err);
    }
  }, []);

  const handleIotStateChange = useCallback(async (id: string, newState: AirConditionerState | ThermometerState) => {
    // Bug 19: 添加 try/catch 错误处理
    try {
      const oldDevice = iotDevices.find(d => d.id === id);
      await iotService.updateState(id, newState, oldDevice?.state);
      const devices = await iotService.getDevices();
      setIotDevices(devices);
    } catch (err) {
      console.error('[MijiaPanel] updateState failed:', err);
    }
  }, [iotDevices]);

  const handleHideDevice = useCallback((id: string) => {
    const updated = [...hiddenDeviceIds, id];
    setHiddenDeviceIds(updated);
    localStorage.setItem('xf_mijia_hidden_devices', JSON.stringify(updated));
  }, [hiddenDeviceIds]);

  const visibleDevices = iotDevices.filter(d => !hiddenDeviceIds.includes(d.id));

  return (
    <GlassCard className="p-5 rounded-[2.5rem] space-y-1.5 border-white/5 bg-black/30 backdrop-blur-3xl">
      <div className="space-y-1">
        <CollapseHeader
          icon={<Home size={10} />}
          label={`米家设备 (${visibleDevices.length})`}
          collapsed={iotCollapsed}
          onToggle={() => setIotCollapsed(v => !v)}
        />
        <CollapseSection collapsed={iotCollapsed}>
          {visibleDevices.length === 0 ? (
            <div className="text-[12px] text-white/40 py-2">
              {t?.noMijiaDevices || '未找到米家设备'}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {visibleDevices.map((device) => (
                <MijiaDeviceCard
                  key={device.id}
                  device={device}
                  compact
                  onTogglePower={handleIotTogglePower}
                  onStateChange={handleIotStateChange}
                  onHide={handleHideDevice}
                />
              ))}
            </div>
          )}
        </CollapseSection>
      </div>
    </GlassCard>
  );
}
